import React from 'react';
import { InsightsSection, InsightsSectionItem } from 'ds/components/Insights';
import { WithContentSlotSidePaddingContainer } from 'ds/components/Layout';
import { IBulletin, ICommercialBulletin, MarketplaceType } from 'utils/entities';
import { ShowAt } from 'consts/breakpoints';
import { ScrollBox } from 'ds/components/carousels/scroll-box';
import Carousel from 'ds/components/carousels/carousel';
import { useLocale } from 'locale';
import { H3 } from 'ds/components/typography';
import styled from '@emotion/styled';
import { OfficeListingsCarouselCard } from './carousel-card';
import { ZoneLink } from 'ds/components/link/styled';
import ArrowLinkIcon from 'assets/svg/link-arrow.svg';
import { Route } from 'config/routes';
import { SpacingScale } from 'ds/theme/spacing';

export interface OfficeListingsCarouselProps {
  marketplace: MarketplaceType;
  excludePoiId: string;
  officeName: string;
  officeId: string;
  listings: Array<IBulletin | ICommercialBulletin>;
  isLoading: boolean;
  headerMTop?: SpacingScale;
  isListingModeB?: boolean;
}


const KEY = Symbol('agentOfficeCarousel');

const Header = styled.div<{ mTop?: SpacingScale; }>`
  ${({ theme, mTop }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${theme.spacing(2)};
    ${mTop ? `padding-top: ${theme.spacing(mTop)};` : ''}
  `}
`;

const Body = styled.div`
  .left-slide-btn,
  .right-slide-btn {
    > div {
      top: calc(50% - 16px);
    }
  }
`;

export const OfficeListingsCarousel: React.FC<OfficeListingsCarouselProps> = (props) => {
  const { listings, isLoading, officeName, officeId, headerMTop, isListingModeB } = props;
  const { t } = useLocale();

  if (!listings || listings.length === 0 || isLoading) return null;
  const hasArrows = listings.length > 2;
  const Container = isListingModeB ? styled.div`` : WithContentSlotSidePaddingContainer;

  return (
    <InsightsSection mTop={0.25} isSolidGrey>
      <InsightsSectionItem staticPadding={isListingModeB}>
        <Container data-auto="agents-office-listings">
          <Header mTop={headerMTop}>
            <H3 weight="bold">{t('unitPage.agentListings.carouselHeader', { officeName })}</H3>
            <ZoneLink
              data-auto="office-listings-office-page-link"
              preserveParams
              routeName={Route.OfficePage}
              routeParams={{ id: officeId }}
            >
              {t('unitPage.agentListings.officeLink')}
              <ArrowLinkIcon />
            </ZoneLink>
          </Header>
          <Body>
            <ShowAt to={2}>
              <ScrollBox
                cacheKey={KEY}
                autoWidth
              >
                {listings.map(({ id, type }) => <OfficeListingsCarouselCard key={id} id={id} type={type} />)}
              </ScrollBox>
            </ShowAt>
            <ShowAt from={3}>
              <Carousel
                isLoading={isLoading}
                hasArrows={hasArrows}
              >
                {listings.map(({ id, type }) => <OfficeListingsCarouselCard key={id} id={id} type={type} />)}
              </Carousel>
            </ShowAt>
          </Body>
        </Container>
      </InsightsSectionItem>
    </InsightsSection>
  );
};
